<script setup lang="ts">
  import {
    ChevronLeftIcon,
    ChevronRightIcon,
  } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { fmt } from '@/functions'

  interface Props {
    modelValue?: Pagination,
    scrollStatus?: boolean,
    customNamePage?: string,
    align?: string,
    labelTop?: boolean,
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: undefined,
    scrollStatus: true,
    customNamePage: 'page',
    align: 'end',
    labelTop: false,
  })

  const route = useRoute()
  const router = useRouter()

  const currentPage = computed(() => Number(props.modelValue?.current_page) || 1)
  const lastPage = computed(() => Number(props.modelValue?.last_page) || 1)
  const routePage = computed(() => route.query[props.customNamePage])

  const safeCurrentPage = computed(() => {
    const cur = currentPage.value
    const last = lastPage.value
    if (cur > last) return last
    if (cur < 1) return 1
    return cur
  })

  const pages = computed(() => {
    const p = [safeCurrentPage.value]
    let i = 1
    while (p.length < 5 && p.length < lastPage.value) {
      if (safeCurrentPage.value - i >= 1) {
        p.unshift(safeCurrentPage.value - i)
      }
      if (safeCurrentPage.value + i <= lastPage.value) {
        p.push(safeCurrentPage.value + i)
      }
      i++
    }
    return p
  })

  const inputPage = ref<string>('')

  watch(
    currentPage,
    (val) => {
      inputPage.value = String(val || 1)
    },
    { immediate: true },
  )

  watch(routePage, () => {
    if (props.scrollStatus) {
      window.scrollTo(0, 0)
    }
  })

  const getRoute = (p: number | string) => {
    const pageNum = Number(p)
    const page = pageNum <= 1 ? undefined : pageNum
    return { ...route, query: { ...route.query, [props.customNamePage]: page } }
  }

  const onInputPage = (e: Event) => {
    const target = e.target as HTMLInputElement
    const val = target.value

    // Izinkan string kosong agar pengguna bisa menghapus angka dengan leluasa
    if (val === '') {
      inputPage.value = ''
      return
    }

    const num = parseInt(val, 10)
    if (isNaN(num)) return

    // Izinkan 0 saat sedang mengetik
    if (num < 0) {
      inputPage.value = '0'
      target.value = '0'
      return
    }

    // Batasi ke halaman maksimal jika melebihi lastPage
    if (lastPage.value && num > lastPage.value) {
      inputPage.value = String(lastPage.value)
      target.value = String(lastPage.value)
      return
    }

    inputPage.value = val
  }

  const goToPage = () => {
    const val = String(inputPage.value).trim()
    if (val === '') {
      inputPage.value = String(currentPage.value || 1)
      return
    }

    let page = parseInt(val, 10)
    if (isNaN(page) || page < 1) {
      page = 1
    } else if (page > lastPage.value) {
      page = lastPage.value
    }

    inputPage.value = String(page)
    if (page !== currentPage.value) {
      router.push(getRoute(page))
    }
  }

  const onBlurPage = () => {
    const val = String(inputPage.value).trim()
    if (val === '' || val === '0') {
      inputPage.value = String(currentPage.value || 1)
      return
    }
    goToPage()
  }
</script>

<template>
  <nav
    v-if="modelValue && lastPage > 1"
    role="navigation"
    aria-label="Pagination Navigation"
    class="w-full">
    <div
      :class="
        labelTop
          ? 'flex flex-col items-center gap-3'
          : 'flex flex-wrap items-center justify-between gap-3'
      ">
      <div class="hidden text-xs text-gray-600 md:block">
        Menampilkan
        <span class="font-semibold text-gray-900">
          {{ fmt.number(modelValue.from) }}-{{ fmt.number(modelValue.to) }}
        </span>
        dari
        <span class="font-semibold text-gray-900">{{ fmt.number(modelValue.total) }}</span>
      </div>

      <div
        class="flex flex-wrap items-center gap-1"
        :class="align === 'end' ? 'ml-auto' : 'mx-auto'">
        <!-- Tombol Halaman Sebelumnya -->
        <router-link
          v-if="currentPage > 1"
          :to="getRoute(currentPage - 1)"
          class="mr-1 flex items-center justify-center rounded-lg border bg-white px-1 py-2 text-xs transition-colors hover:bg-gray-100"
          style="min-width: 2rem"
          title="Halaman Sebelumnya">
          <chevron-left-icon class="size-4" />
        </router-link>
        <span
          v-else
          class="mr-1 flex cursor-not-allowed select-none items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-1 py-2 text-xs text-gray-300"
          style="min-width: 2rem"
          title="Halaman Sebelumnya">
          <chevron-left-icon class="size-4" />
        </span>

        <!-- Halaman 1 (Start page jika belum tercakup dalam daftar angka) -->
        <template v-if="pages[0] > 1">
          <router-link
            :to="getRoute(1)"
            class="mr-1 flex items-center justify-center rounded-lg border !bg-white px-1 py-2 text-xs !text-black transition-colors hover:!bg-gray-100"
            style="min-width: 2rem"
            title="Halaman 1">
            1
          </router-link>
          <span
            v-if="pages[0] > 2"
            class="mr-1 flex select-none items-center justify-center px-1 py-2 text-xs text-gray-400"
            style="min-width: 1.5rem">
            ...
          </span>
        </template>

        <!-- Deretan Halaman Sekitar Halaman Aktif -->
        <template
          v-for="page in pages"
          :key="page">
          <!-- Halaman Aktif: Block Hitam yang Berisi Input Halaman -->
          <form
            v-if="safeCurrentPage === page"
            class="mr-1 flex h-[34px] items-center justify-center rounded-lg border border-black bg-black px-1.5 text-xs text-white shadow-sm"
            style="min-width: 2rem"
            @submit.prevent="goToPage">
            <span class="mr-0.5 select-none text-xs font-semibold text-white/70">#</span>
            <input
              v-model="inputPage"
              type="number"
              min="0"
              :max="lastPage"
              class="h-full bg-transparent text-center text-xs font-semibold text-white focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              :style="{ width: `${Math.max(1, String(inputPage).length) * 8 + 6}px` }"
              :title="`Halaman ${currentPage} dari ${lastPage}. Ketik angka lalu tekan Enter.`"
              @focus="($event.target as HTMLInputElement).select()"
              @input="onInputPage"
              @blur="onBlurPage"
              @keydown.enter.prevent="goToPage">
          </form>

          <!-- Halaman Lainnya: Link Halaman Biasa -->
          <router-link
            v-else
            :to="getRoute(page)"
            class="mr-1 flex items-center justify-center rounded-lg border !bg-white px-1 py-2 text-xs !text-black transition-colors hover:!bg-gray-100"
            style="min-width: 2rem">
            {{ fmt.number(page) }}
          </router-link>
        </template>

        <!-- Halaman Terakhir (End page jika belum tercakup dalam daftar angka) -->
        <template v-if="pages[pages.length - 1] < lastPage">
          <span
            v-if="pages[pages.length - 1] < lastPage - 1"
            class="mr-1 flex select-none items-center justify-center px-1 py-2 text-xs text-gray-400"
            style="min-width: 1.5rem">
            ...
          </span>
          <router-link
            :to="getRoute(lastPage)"
            class="mr-1 flex items-center justify-center rounded-lg border !bg-white px-1 py-2 text-xs !text-black transition-colors hover:!bg-gray-100"
            style="min-width: 2rem"
            :title="`Halaman Terakhir (${lastPage})`">
            {{ fmt.number(lastPage) }}
          </router-link>
        </template>

        <!-- Tombol Halaman Berikutnya -->
        <router-link
          v-if="currentPage < lastPage"
          :to="getRoute(currentPage + 1)"
          class="mr-1 flex items-center justify-center rounded-lg border bg-white px-1 py-2 text-xs transition-colors hover:bg-gray-100"
          style="min-width: 2rem"
          title="Halaman Berikutnya">
          <chevron-right-icon class="size-4" />
        </router-link>
        <span
          v-else
          class="mr-1 flex cursor-not-allowed select-none items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-1 py-2 text-xs text-gray-300"
          style="min-width: 2rem"
          title="Halaman Berikutnya">
          <chevron-right-icon class="size-4" />
        </span>
      </div>
    </div>
  </nav>
</template>

<style lang="postcss"></style>
